// ---------------------------------------------------------------------------
// The random bot — random legal orders, the sanity baseline
// ---------------------------------------------------------------------------
//
// Draws a random plan from the pure order functions and keeps it only if
// validateOrders accepts it (redrawing an illegal one; after 20 misses it
// just deploys). Used as the search bot's baseline opponent and as its fast
// model of the opponent's orders.

import { rules } from '../config.js'
import { moveUnit, deployArmy, buildFort, canAttack, canPlaceDropZone, requiredDropZones } from '../gameLogic.js'
import { eligibleBaseCenters, baseFootprint } from '../subplotBases.js'
import { plotSubIndices, plotOf, isStructureDestroyed, battlefieldColumn } from '../subplotGrid.js'
import { validateOrders } from '../validateOrders.js'
import { pick, shuffled } from './rng.js'

// Open airborne slots on a board (the hook's openSlotCount).
export const openSlots = (board) => board.reduce((sum, bf) =>
    sum + Math.max(0, bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))), 0)

// A random subplot in corridor `col` (plot rows 0–4).
const randomSubplotIn = (rng, col) => pick(rng, plotSubIndices(col, Math.floor(rng() * 5)))

// Cells that may host a rocket or launch a plane: live cells of standing
// structures.
export function assetCells(hf) {
    const out = []
    for (const [id, st] of Object.entries(hf?.structures ?? {})) {
        if (isStructureDestroyed(hf, id)) continue
        for (const s of st.footprint) {
            if (!hf.subplots[s]?.destroyed && hf.subplots[s]?.structureId === id) out.push({ s, st })
        }
    }
    return out
}

// One player's random legal orders for the round: a random plan, checked by
// validateOrders; an illegal one (rare — a deploy or drop zone that found no
// room in its tries) is drawn again, and after 20 misses the player just
// deploys.
export function randomOrders(player, rng) {
    for (let tries = 0; tries < 20; tries++) {
        const orders = randomPlan(player, rng)
        if (validateOrders(player, orders).ok) return orders
    }
    return deployOnly(player)
}

// The plainest legal plan: the round's deploys, front to back, and the drop
// zones owed, nothing bought, nobody attacking.
export function deployOnly(player) {
    let board = JSON.parse(JSON.stringify(player.board))
    let owed = rules().armiesPerRound
    for (let col = 0; owed > 0 && col < board.length; col++) {
        let r
        while (owed > 0 && (r = deployArmy(board, board[col].id, owed)).success) { board = r.boardArr; owed = r.armiesToDeploy }
    }
    const dropZones = []
    const owedZones = Math.min(requiredDropZones(board), openSlots(board))
    for (let col = 0; dropZones.length < owedZones && col < board.length; col++) {
        while (dropZones.length < owedZones && canPlaceDropZone(board, col, dropZones).valid) dropZones.push(col)
    }
    return { board, targetPlots: Array(board.length).fill(null), targetSubplots: Array(board.length).fill(null), dropZones, rockets: [], drones: [] }
}

function randomPlan(player, rng) {
    let board = JSON.parse(JSON.stringify(player.board))
    const hf = player.homefront

    // 1. Moves.
    for (const col of shuffled(rng, board.map((_, i) => i))) {
        if (rng() < 0.25) {
            const r = moveUnit(board, board[col].id, rng() < 0.5 ? 'left' : 'right')
            if (r.success) board = r.boardArr
        }
    }

    // 2. Broods (before any attack is declared: no brood in an attacking corridor).
    for (let col = 0; col < board.length; col++) {
        const bf = board[col]
        if (bf.isAttacking || (bf.armyQty || 0) < 2 || rng() >= 0.3) continue
        const pending = new Set((bf.pendingBaseBuilds ?? []).flatMap(p => baseFootprint(p.centerS) ?? []))
        const centers = eligibleBaseCenters(hf, col).filter(c => !(baseFootprint(c) ?? []).some(s => pending.has(s)))
        if (!centers.length) continue
        const r = buildFort(board, bf.id, pick(rng, centers))
        if (r.success) board = r.boardArr
    }

    // 3. Deploys: every one owed, where there is room.
    let owed = rules().armiesPerRound
    for (let tries = 0; owed > 0 && tries < 50; tries++) {
        const r = deployArmy(board, board[Math.floor(rng() * board.length)].id, owed)
        if (r.success) { board = r.boardArr; owed = r.armiesToDeploy }
    }

    // 4. Attacks, each aimed at a subplot in its own corridor.
    const targetPlots = Array(board.length).fill(null)
    const targetSubplots = Array(board.length).fill(null)
    for (let col = 0; col < board.length; col++) {
        if (rng() >= 0.45 || !canAttack(board[col]).valid) continue
        board[col] = { ...board[col], isAttacking: true }
        const s = randomSubplotIn(rng, col)
        targetSubplots[col] = s
        targetPlots[col] = plotOf(s).row
    }

    // 5. Drop zones: exactly the number owed (capped by open slots).
    const dropZones = []
    const owedZones = Math.min(requiredDropZones(board), openSlots(board))
    for (let tries = 0; dropZones.length < owedZones && tries < 100; tries++) {
        const col = Math.floor(rng() * board.length)
        if (canPlaceDropZone(board, col, dropZones).valid) dropZones.push(col)
    }

    // 6. Rockets and recon planes, only as far as yield covers.
    const { rocketCost, droneCost } = rules()
    let spendable = player.yield ?? 0
    const rockets = []
    const drones = []
    const used = new Set()
    // Rockets first (a player saving up for one), then at most one plane.
    const cells = shuffled(rng, assetCells(hf).filter(({ s, st }) => !(st.rocketCells || []).includes(s)))
    for (const { s } of cells) {
        if (spendable < rocketCost || rng() >= 0.6) break
        rockets.push(s); used.add(s); spendable -= rocketCost
    }
    const launch = cells.find(({ s }) => !used.has(s))
    if (launch && spendable >= droneCost && rng() < 0.2) {
        drones.push({ launchS: launch.s, targetS: randomSubplotIn(rng, battlefieldColumn(launch.s)) })
        spendable -= droneCost
    }

    return { board, targetPlots, targetSubplots, dropZones, rockets, drones }
}

